import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { UserPool } from './auth-config';
import { CognitoUserAttribute, CognitoUser } from 'amazon-cognito-identity-js';
import { Box, Button, Container, CssBaseline, FormControl, LinearProgress, Paper, SelectChangeEvent, ThemeProvider, Typography, createTheme } from '@mui/material';
import Header from './cognitoHeader';
import { AuthenticationDetails } from 'amazon-cognito-identity-js';
import { Link, useNavigate } from "react-router-dom";
import { MuiOtpInput } from 'mui-one-time-password-input';
import { grey } from '@mui/material/colors';
import { motion } from 'framer-motion';
import AuthFlowSelect from './AuthFlowSelect';
import { EmailInput, PasswordInput } from './InputFields';
import { OwnID, WidgetType } from '@ownid/react';


const theme = createTheme();

function PasswordErrorMsg() {
    return (
        <Typography style={{
            color: 'red',
            fontSize: '12px',
            marginBottom: '12px',
            textAlign: "left",
            padding: "8px",
        }}>Password must be at least 8 characters long and contain:
            <br />- At least one uppercase letter
            <br />- At least one lowercase letter
            <br />- At least one number
            <br />- At least one special character
        </Typography>
    );
}

function SignUp() {
    const [password, setPassword] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [otp, setOtp] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [showPasswordErr, setShowPasswordErr] = useState<boolean>(false);
    const [user, setUser] = useState<CognitoUser | null>(null);
    const [step, setStep] = useState<number>(1); // 1: Sign up, 2: Confirm OTP
    const [ownIdData, setOwnIdData] = useState<string>('');
    const emailField = useRef(null);
    const signUpBtnRef = useRef<HTMLButtonElement>(null);
    const passwordField = useRef(null);
    const navigate = useNavigate();
    const [showOwnID, setShowOwnID] = useState<boolean>(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const ownIDParam = urlParams.get('pk');
        setShowOwnID(!(ownIDParam === 'false'));
    }, []);

    function autoLogin(email: string, password: string) {
        const authDetails = new AuthenticationDetails({
            Username: email,
            Password: password,
        });
        const cognitoUser = new CognitoUser({
            Username: email,
            Pool: UserPool,
        });
        cognitoUser.authenticateUser(authDetails, {
            onSuccess: function (result) {
                console.log('access token + ' + result.getAccessToken().getJwtToken());
                navigate("/shop");
            },
            onFailure: function (err) {
                console.log(err);
            }
        })
    }

    const handleSignUp = useCallback(() => {
        setLoading(true);
        const attributeList = [
            new CognitoUserAttribute({ Name: 'email', Value: email }),
            new CognitoUserAttribute({ Name: 'custom:forterToken', Value: window.ftrToken ?? null }),
            new CognitoUserAttribute({ Name: 'custom:userAgent', Value: navigator.userAgent }),
        ];
        const ownIdData = localStorage.getItem('ownIdData')
        if (ownIdData) {
            const token = JSON.parse(ownIdData).data;
            attributeList.push(new CognitoUserAttribute({ Name: 'custom:ownIdData', Value: token }));
        }
        UserPool.signUp(email, password, attributeList, [], (err: any, result: any) => {
            setShowPasswordErr(false);
            setLoading(false);
            if (err) {
                if (err.code === "InvalidPasswordException") {
                    setShowPasswordErr(true);
                } else {
                    setMessage(`Sign-up failed, please try again later.`);
                }
                return;
            } else if (result.userConfirmed === false) {
                setMessage('');
                setUser(result.user);
                setStep(2);
            } else if (result.userConfirmed === true) {
                setMessage('Sign-up successful! Logging in...');
                autoLogin(email, password);
            }
        });
    }, [setLoading, email, password, loading]);

    const handleOTP = useCallback((otpInput: string) => {
        setLoading(true);
        if (user) {
            console.log(`Loading: ${loading}`);
            user.confirmRegistration(otpInput, true, (err, result) => {
                setLoading(false);
                if (err) {
                    setMessage('OTP verification failed, please try again.');
                    return;
                }
                setMessage('Verification successful! Redirecting to login...');
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            });
        } else {
            setLoading(false);
            setMessage('User not found, please sign up again.');
        }
    }, [user, navigate, loading, setLoading]);

    const handleResendCode = () => {
        if (user) {
            user.resendConfirmationCode((err, result) => {
                if (err) {
                    setMessage('Failed to resend OTP, please try again.');
                    return;
                }
                setMessage('OTP has been resent. Please check your email.');
            });
        }
    };
    function handleFlowChange(event: SelectChangeEvent<any>): void {
        const credentials = event.target.value;
        setEmail(credentials.email);
        setPassword(credentials.password);
    }

    const onRegister = useCallback((ownIdData: string) => {
        localStorage.setItem('ownIdData', JSON.stringify(ownIdData));
        setOwnIdData(JSON.stringify(ownIdData));
    }, []);

    useEffect(() => {
        if (ownIdData && ownIdData !== "") {
            handleSignUp();
        }
    }, [ownIdData])

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Container maxWidth="lg" sx={{
                marginBottom: "24px",
            }}>
                <Header title="" sections={[]} />
                <Box component="img" sx={{
                    position: {
                        "md": 'absolute'
                    },
                }} src="/RocketElectronics.svg" />
                <Container maxWidth="sm">
                    <main>
                        <AuthFlowSelect formType="signup" handleFlowChange={handleFlowChange} />
                        <Paper elevation={4} sx={{
                            paddingLeft: {
                                xs: "12px",
                                md: "48px",
                            },
                            paddingRight: {
                                xs: "12px",
                                md: "48px",
                            },
                            marginTop: '50px',
                            borderRadius: '20px',
                            background: 'linear-gradient(to top, #f9f9f9, #ffffff)',
                        }}>
                            <Box display="flex" flexDirection="column" justifyContent="start" alignItems="start">
                                <h2 style={{
                                    marginBottom: '10px',
                                }}>Sign Up</h2>
                                <Typography style={{
                                    marginBottom: '40px',
                                    fontWeight: 400,
                                }} color="gray">{step === 1 ? "Please enter your details." : `Enter the 6 digit code sent to ${email}`}    </Typography>
                            </Box>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    {step === 1 && (
                                        <FormControl fullWidth>
                                            <EmailInput ref={emailField} value={email} onChange={(e) => setEmail(e.target.value)} />
                                            <PasswordInput ref={passwordField} value={password} onChange={(e) => setPassword(e.target.value)} />
                                            {showPasswordErr && <PasswordErrorMsg />}
                                            <Button
                                                ref={signUpBtnRef}
                                                style={{
                                                    marginTop: '12px',
                                                    height: "48px",
                                                    backgroundColor: loading ? grey[300] : '#333333',
                                                    textTransform: "none",
                                                    borderRadius: "10px",
                                                    color: loading ? grey[500] : 'white',
                                                }}
                                                fullWidth
                                                variant="contained"
                                                onClick={handleSignUp}
                                                disabled={loading}
                                            >
                                                <b>Sign Up</b>
                                            </Button>
                                            {showOwnID && <OwnID type={WidgetType.Register}
                                                loginIdField={emailField}
                                                passwordField={passwordField}
                                                passwordFieldBinding={true}
                                                onError={(error) => console.error(error)}
                                                onRegister={onRegister} />}
                                        </FormControl>
                                    )}
                                    {step === 2 && (
                                        <>
                                            <MuiOtpInput
                                                sx={{
                                                    marginBottom: '12px',
                                                    width: '100%',
                                                    "& .MuiOtpInput-TextField input": {
                                                        fontSize: {
                                                            xs: '16px',
                                                            sm: '24px'
                                                        },
                                                        padding: {
                                                            xs: '8px',
                                                            sm: '16px',
                                                        },
                                                        height: {
                                                            xs: '24px',
                                                            sm: '20px',
                                                            md: '24px',
                                                            lg: '24px',
                                                        },
                                                    }
                                                }}
                                                TextFieldsProps={{
                                                    sx: {
                                                        padding: 0
                                                    },
                                                    inputProps: {
                                                        autoComplete: "one-time-code",
                                                        inputMode: "numeric",
                                                        pattern: "[0-9]*"
                                                    },
                                                    type: "tel"
                                                }}
                                                className="otp-input"
                                                value={otp}
                                                length={6}
                                                gap={1}
                                                onChange={(value) => setOtp(value)}
                                                onComplete={handleOTP}
                                            />
                                            <Button
                                                style={{
                                                    marginTop: '12px',
                                                    height: "48px",
                                                    backgroundColor: loading ? grey[300] : '#333333',
                                                    textTransform: "none",
                                                    borderRadius: "10px",
                                                    color: loading ? grey[500] : 'white',
                                                }}
                                                fullWidth
                                                variant="contained"
                                                onClick={() => handleOTP(otp)}
                                                disabled={loading}
                                            >
                                                <b>Next</b>
                                            </Button>
                                        </>
                                    )}
                                    {step === 1 && (
                                        <Typography style={{
                                            marginTop: '24px',
                                            marginBottom: '12px',
                                            color: 'gray',
                                            fontSize: '12px',
                                        }}>Already have an account? <Link style={{ color: 'inherit', textDecoration: 'underline' }} to="/login">login</Link></Typography>
                                    )}
                                    {loading && <LinearProgress sx={{
                                        width: '100%',
                                        marginTop: '24px',
                                        marginBottom: '12px',
                                    }} />}
                                    {!loading && <p>{message}</p>}
                                    {step === 2 && (
                                        <Typography style={{
                                            marginTop: '24px',
                                            marginBottom: '12px',
                                            color: 'gray',
                                            fontSize: '12px',
                                        }}>Didn't receive an OTP?<button style={{
                                            textTransform: "none",
                                            border: "none",
                                            textDecoration: "underline",
                                            backgroundColor: "transparent",
                                            color: "gray",
                                            cursor: "pointer",
                                        }} onClick={handleResendCode}>send again</button></Typography>
                                    )}
                                </Box>
                            </motion.div>
                        </Paper>
                    </main>
                </Container>
            </Container>
        </ThemeProvider>
    );
}

export default SignUp;
